import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MarkdownRenderer } from '@/components/shared/MarkdownRenderer';
import { PdfExportButton } from '@/components/shared/PdfExportButton';
import type { PdfExportData } from '@/utils/pdf-export';

interface ActionButton {
  text: string;
  onClick: () => void;
}

interface AiDetailPanelProps {
  title: string;
  content: string | null | undefined;
  canEdit?: boolean;
  canDelete: boolean;
  onEdit?: () => void;
  onDelete: () => void;
  editButtonText?: string;
  actionButtons?: ActionButton[];
  pdfExportData?: PdfExportData;
  tooltipEditText?: string;
  tooltipDeleteText?: string;
  emptyText?: string;
}

export function AiDetailPanel({
  title,
  content,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  editButtonText,
  actionButtons,
  pdfExportData,
  tooltipEditText,
  tooltipDeleteText,
  emptyText,
}: AiDetailPanelProps) {
  const { t } = useTranslation('common');
  const [tooltipVisible, setTooltipVisible] = useState<string | null>(null);

  const finalTooltipEditText = tooltipEditText ?? t('aiDetailPanel.tooltipEdit');
  const finalTooltipDeleteText = tooltipDeleteText ?? t('aiDetailPanel.tooltipDelete');
  const finalEmptyText = emptyText ?? t('aiDetailPanel.emptyText');

  return (
    <div className="flex-1 min-w-0">
      <div className="bg-[#f4f4f3] rounded-[16px] p-[20px] md:p-[40px] max-w-[720px] mx-auto w-full">
        {/* Header with title and delete */}
        <div className="flex items-start justify-between mb-[40px] gap-[16px]">
          <h2 className="font-manrope font-bold text-[24px] md:text-[32px] leading-[32px] md:leading-[40px] text-[#0d0d0d] flex-1 break-words">
            {title}
          </h2>
          <button
            onClick={() => canDelete && onDelete()}
            disabled={!canDelete}
            onMouseEnter={() => !canDelete && setTooltipVisible('delete')}
            onMouseLeave={() => setTooltipVisible(null)}
            className={`relative w-[24px] h-[24px] flex-shrink-0 ${!canDelete ? 'cursor-not-allowed' : 'cursor-pointer hover:opacity-70'} transition-opacity`}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M21 5.98047C17.67 5.65047 14.32 5.48047 10.98 5.48047C9 5.48047 7.02 5.58047 5.04 5.78047L3 5.98047" stroke="#565858" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M8.5 4.97L8.72 3.66C8.88 2.71 9 2 10.69 2H13.31C15 2 15.13 2.75 15.28 3.67L15.5 4.97" stroke="#565858" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M18.85 9.14L18.2 19.21C18.09 20.78 18 22 15.21 22H8.79C6 22 5.91 20.78 5.8 19.21L5.15 9.14" stroke="#565858" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M10.33 16.5H13.66" stroke="#565858" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M9.5 12.5H14.5" stroke="#565858" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>

            {tooltipVisible === 'delete' && !canDelete && (
              <div className="absolute top-[-60px] left-1/2 -translate-x-1/2 bg-[rgba(13,13,13,0.8)] px-[24px] py-[12px] rounded-[16px] whitespace-nowrap z-10">
                <p className="font-segoe text-[14px] leading-[20px] text-white text-center">
                  {finalTooltipDeleteText}
                </p>
              </div>
            )}
          </button>
        </div>

        {/* Content */}
        <div className="mb-[40px]">
          {content ? (
            <MarkdownRenderer content={content} />
          ) : (
            <p className="font-segoe text-[16px] leading-[24px] text-[#565858]">
              {finalEmptyText}
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col md:flex-row gap-[16px]">
          {onEdit && editButtonText && (
            <button
              onClick={() => canEdit && onEdit()}
              disabled={!canEdit}
              onMouseEnter={() => !canEdit && setTooltipVisible('edit')}
              onMouseLeave={() => setTooltipVisible(null)}
              className={`group relative px-[24px] py-[12px] rounded-[16px] flex items-center justify-center gap-[8px] transition-colors text-[#2197f1] ${canEdit
                ? 'bg-[rgba(33,151,241,0.2)] hover:bg-[#2197f1] hover:text-white'
                : 'bg-[rgba(33,151,241,0.2)] cursor-not-allowed opacity-60'
                }`}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M11 2H9C4 2 2 4 2 9V15C2 20 4 22 9 22H15C20 22 22 20 22 15V13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M16.04 3.02001L8.16 10.9C7.86 11.2 7.56 11.79 7.5 12.22L7.07 15.23C6.91 16.32 7.68 17.08 8.77 16.93L11.78 16.5C12.2 16.44 12.79 16.14 13.1 15.84L20.98 7.96001C22.34 6.60001 22.98 5.02001 20.98 3.02001C18.98 1.02001 17.4 1.66001 16.04 3.02001Z" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M14.91 4.1499C15.58 6.5399 17.45 8.4099 19.85 9.0899" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <p className="font-segoe font-semibold text-[16px] leading-[24px] text-current">
                {editButtonText}
              </p>

              {tooltipVisible === 'edit' && !canEdit && (
                <div className="absolute top-[-60px] left-1/2 -translate-x-1/2 bg-[rgba(13,13,13,0.8)] px-[24px] py-[12px] rounded-[16px] whitespace-nowrap z-10">
                  <p className="font-segoe text-[14px] leading-[20px] text-white text-center">
                    {finalTooltipEditText}
                  </p>
                </div>
              )}
            </button>
          )}

          {actionButtons?.map((btn, index) => (
            <button
              key={index}
              onClick={btn.onClick}
              className="px-[24px] py-[12px] rounded-[16px] bg-[rgba(33,151,241,0.2)] hover:bg-[#2197f1] text-[#2197f1] hover:text-white transition-colors"
            >
              <p className="font-segoe font-semibold text-[16px] leading-[24px] text-current text-center">
                {btn.text}
              </p>
            </button>
          ))}

          {pdfExportData && <PdfExportButton data={pdfExportData} />}
        </div>
      </div>
    </div>
  );
}
